import React, { Fragment, useMemo, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { AskRequest, User, ResolvedLink, EntityLink } from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';
import { useParams } from 'react-router-dom';
import { format, parse } from 'date-fns';
import CustomModal from '../../../../../../../components/CustomModal/CustomModal';
import MessagesCard from '../../../../Cards/MessagesCard/MessagesCard';
import {
  buildMessagesCardPropsWithRequest,
  AskRequestQueueComment,
} from '../../../../Cards/MessagesCard/messagesCardFactory';
import { useMessageActions } from '../../../../Cards/MessagesCard/hooks';
import { extractUsersFromComments, mergeActiveAndCommentUsers } from '../../../../../Queue/utils/userUtils';
import { useGetAllUsersByLocation } from 'hooks';
import { ProfileEntryParams } from 'presentation/components/NewTable/types';
import { groupMessagesByDate } from 'presentation/utils';
import useFetchLinks from 'hooks/useFetchLinks';
import type { MessageDetailsLink } from '../../../../Cards/MessagesCard/subcomponents/MessageContent/analyzeMessageContent';
import ConfirmModal from '../../../../../ConfirmModal/ConfirmModal';
import './MessageList.scss';
import { IconChevron, IconChevronDown } from 'components/Icons';
import { prefixClass } from 'lib/utils';
import { PendingMessage, PendingMessageData } from './PendingMessage';

interface MessageListProps {
  /** The AskRequest - optional for new flagged transactions that don't have a request yet */
  request?: AskRequest;
  comments?: AskRequestQueueComment[];
  fixedHeight?: boolean;
  mobile?: boolean;
  pendingMessage?: PendingMessageData | null;
}

/** Ref interface for external scroll control */
export interface MessageListRef {
  scrollToTop: () => void;
  scrollToBottom: () => void;
}

interface MessageCardWrapperProps {
  comment: AskRequestQueueComment;
  request: AskRequest;
  isFirstMessage: boolean;
  userId: string;
  locationId: string;
  allUsers: User[];
  details?: any;
}

/**
 * Wrapper component that adds edit/delete functionality to a MessagesCard
 */
const MessageCardWrapper: React.FC<MessageCardWrapperProps> = ({
  comment,
  request,
  isFirstMessage,
  userId,
  locationId,
  allUsers,
  details,
}) => {
  const {
    isEditing,
    showDeleteModal,
    handleEdit,
    handleDelete,
    handleSaveEdit,
    handleConfirmDelete,
    handleCancelEdit,
    handleCancelDelete,
  } = useMessageActions({
    messageId: comment.id || '',
    status: request.status,
    locationId,
  });

  // Build props using the factory
  const messageCardProps = buildMessagesCardPropsWithRequest(comment, request, {
    currentUserId: userId,
    users: allUsers,
    isFirstMessage,
    details,
  });

  // Modal styles
  const modalStyle = {
    content: {
      width: '600px',
      height: 'fit-content',
    },
  };

  return (
    <>
      <MessagesCard
        {...messageCardProps}
        messageId={comment.id}
        isEditing={isEditing}
        showDeleteModal={showDeleteModal}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onConfirmDelete={handleConfirmDelete}
        onCancelDelete={handleCancelDelete}
      />

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <CustomModal isOpen={showDeleteModal} onRequestClose={handleCancelDelete} style={modalStyle}>
          <ConfirmModal title="Delete Message" onConfirm={handleConfirmDelete} onClose={handleCancelDelete}>
            <p>Are you sure you want to delete this message? This action cannot be undone.</p>
          </ConfirmModal>
        </CustomModal>
      )}
    </>
  );
};

const MessageList = forwardRef<MessageListRef, MessageListProps>(function MessageList(
  { request, comments = [], fixedHeight = false, mobile = false, pendingMessage },
  ref
) {
  const { userId } = useAuthContext();
  const { locationId } = useParams<ProfileEntryParams>();
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const prefix = prefixClass('message-list');

  // Fetch all users from the location (includes deleted/deactivated users)
  const { data: usersFetched } = useGetAllUsersByLocation(locationId);

  // Fetch links for the request (for first message details)
  const { data: links, isLoading: linksLoading, error: linksError } = useFetchLinks(request || null, locationId);

  // Group messages by date
  const groupedMessages = useMemo(() => groupMessagesByDate(comments), [comments]);

  // Extract all unique users from comments for mention support
  const allUsersFromComments = useMemo(() => extractUsersFromComments(comments), [comments]);

  // Merge active users with users from comments to include deactivated users
  const allUsers = useMemo(
    () => mergeActiveAndCommentUsers(usersFetched as User[] | undefined, allUsersFromComments),
    [usersFetched, allUsersFromComments]
  );

  // Auto-scroll to bottom when comments change or pending message appears
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [comments, pendingMessage]);

  // Ensure scroll to bottom on mount
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }

    const scrollTimer = setTimeout(() => {
      if (messageListRef.current) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, []);

  // Handle scroll position to show/hide scroll buttons
  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const THRESHOLD = 150;

      // Show scroll-to-top if scrolled down more than threshold
      setShowScrollToTop(scrollTop > THRESHOLD);

      // Show scroll-to-bottom if not near bottom (more than threshold from bottom)
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setShowScrollToBottom(distanceFromBottom > THRESHOLD);
    };

    container.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo({ top: messageListRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  // Expose scroll methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      scrollToTop,
      scrollToBottom,
    }),
    []
  );

  const hasMessages = comments.length > 0;

  return (
    <div className={prefix('wrapper')}>
      {/* Scroll to top button - hidden on mobile (title tap handles it) */}
      {showScrollToTop && !mobile && (
        <button className="message-list__scroll-button message-list__scroll-button--top" onClick={scrollToTop}>
          <div className="message-list__scroll-button-icon-wrapper">
            <IconChevronDown />
          </div>
          Details
        </button>
      )}

      {/* Scroll to bottom button. Disabled right now until it can be properly added to the Composer */}
      {/* {showScrollToBottom && (
        <button className="message-list__scroll-button message-list__scroll-button--bottom" onClick={scrollToBottom}>
          ↓
        </button>
      )} */}

      <div
        className={`message-list ${fixedHeight && hasMessages ? 'fixed-height' : ''} ${!hasMessages ? 'no-messages' : ''} ${mobile ? 'mobile' : ''}`}
        ref={messageListRef}
      >
        {!hasMessages ? (
          <p className={prefix('empty')}>No messages yet</p>
        ) : (
          Object.entries(groupedMessages).map(([dateStr, messages], groupIndex) => (
            <Fragment key={dateStr}>
              {/* Date divider */}
              <div className="message-list__date-divider-container">
                <div className="message-list__date-divider">
                  {format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'LLL do, yyyy')}
                </div>
              </div>

              {/* Messages for this date */}
              {messages.map((comment, idx) => {
                const isFirstMessage = idx === 0 && groupIndex === 0;

                // For the first message, enhance details with links data
                let details = undefined;
                if (isFirstMessage && request) {
                  // Extract sectionAmount from links data if available
                  const sectionAmount = links ? (links as ResolvedLink<EntityLink>[])[0]?.sectionAmount : undefined;
                  const baseDetails = buildMessagesCardPropsWithRequest(comment, request, {
                    currentUserId: userId || undefined,
                    users: allUsers,
                    isFirstMessage: true,
                    sectionAmount,
                  }).details;

                  if (baseDetails) {
                    details = {
                      ...baseDetails,
                      links: links as MessageDetailsLink[] | undefined,
                      linksLoading,
                      linksError: !!linksError,
                    };
                  }
                }

                return (
                  <MessageCardWrapper
                    key={comment.id || `message-${idx}`}
                    comment={comment}
                    request={request!}
                    isFirstMessage={isFirstMessage}
                    userId={userId || ''}
                    locationId={locationId || ''}
                    allUsers={allUsers}
                    details={details}
                  />
                );
              })}
            </Fragment>
          ))
        )}

        {/* Pending message indicator */}
        {pendingMessage && <PendingMessage message={pendingMessage} />}
      </div>
    </div>
  );
});

export { MessageList };
export default MessageList;
